<template>
  <div id="app">
    <!-- 只在非认证页面显示导航栏 -->
    <nav v-if="showNavigation" class="bg-white shadow-lg border-b-4 border-red-600">
      <div class="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div class="flex justify-between items-center h-14 sm:h-16">
          <div class="flex items-center min-w-0">
            <h1 class="text-lg sm:text-xl lg:text-2xl font-bold text-red-600 truncate">
              菜单模板管理系统
            </h1>
          </div>

          <!-- 移动端菜单按钮 -->
          <div class="flex items-center space-x-2 sm:space-x-4">
            <!-- 桌面端导航 -->
            <div class="hidden sm:flex items-center space-x-4">
              <router-link
                to="/"
                class="text-gray-700 hover:text-red-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                模板列表
              </router-link>
              <router-link
                to="/templates/create"
                class="btn-primary text-sm"
              >
                创建模板
              </router-link>

              <!-- 认证状态和退出按钮 -->
              <div class="flex items-center space-x-3 ml-4 pl-4 border-l border-gray-200">
                <div class="flex items-center space-x-2 text-sm text-gray-600">
                  <Shield class="w-4 h-4 text-green-500" />
                  <span>已认证</span>
                </div>
                <button
                  @click="handleLogout"
                  class="text-gray-500 hover:text-red-600 p-2 rounded-md transition-colors"
                  title="退出登录"
                >
                  <LogOut class="w-4 h-4" />
                </button>
              </div>
            </div>

            <!-- 移动端导航 -->
            <div class="flex sm:hidden items-center space-x-2">
              <router-link
                to="/"
                class="text-gray-700 hover:text-red-600 p-2 rounded-md transition-colors"
                title="模板列表"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"></path>
                </svg>
              </router-link>
              <router-link
                to="/templates/create"
                class="text-red-600 hover:text-red-700 p-2 rounded-md transition-colors"
                title="创建模板"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                </svg>
              </router-link>
              <button
                @click="handleLogout"
                class="text-gray-500 hover:text-red-600 p-2 rounded-md transition-colors"
                title="退出登录"
              >
                <LogOut class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <main :class="showNavigation ? 'max-w-7xl mx-auto py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-8' : ''">
      <router-view />
    </main>

    <!-- 全局 Toast 组件 -->
    <Toast ref="toastRef" />

    <!-- 全局确认对话框 -->
    <ConfirmDialog
      :is-open="confirmDialog.isOpen.value"
      :type="confirmDialog.options.value.type"
      :message="confirmDialog.options.value.message"
      :description="confirmDialog.options.value.description"
      :confirm-text="confirmDialog.options.value.confirmText"
      :cancel-text="confirmDialog.options.value.cancelText"
      @confirm="confirmDialog.handleConfirm"
      @cancel="confirmDialog.handleCancel"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Shield, LogOut } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { useToast, useConfirm } from '@/composables/useModal'
import Toast from '@/components/Toast.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { setToastInstance } = useToast()
const confirmDialog = useConfirm()
const toastRef = ref()

// 是否显示导航栏（认证页面不显示）
const showNavigation = computed(() => {
  return route.name !== 'Auth'
})

// 初始化 Toast 实例
onMounted(() => {
  if (toastRef.value) {
    setToastInstance(toastRef.value)
  }
})

// 处理退出登录
const handleLogout = async () => {
  const result = await confirmDialog.confirm({
    type: 'warning',
    message: '确定要退出登录吗？',
    description: '退出后需要重新输入密钥登录',
    confirmText: '退出登录'
  })

  if (result) {
    authStore.clearAuth()
    router.push('/auth')
  }
}
</script>

<style scoped>
/* Component-specific styles if needed */
</style>
